import * as React from 'react';
import styled from '@emotion/styled';
import { Checkbox } from '@hotjar/webapp/components/Checkbox';
import { Tooltip } from '@hotjar/webapp/components/Tooltip';
import { is } from '@hotjar/webapp/lib/function';

type Size = 's' | 'm';

interface Option<TOptionValue> {
  label: string;
  value: TOptionValue;
}

interface Props<TOptionValue> {
  size?: Size;
  disabled?: boolean;
  options: Array<Option<TOptionValue>>;
  value: Array<TOptionValue>;
  onChange: (selected: Array<TOptionValue>) => void;
  minChecked?: number;
  minCheckedTooltip?: string;
}

const List = styled.ul`
  position: relative;
  display: block;
  list-style-type: none;
  margin: 0;
  padding: 0;
  width: max-content;
  max-width: 100%;
`;

const ListItem = styled.li`
  margin-bottom: 8px;
  position: relative;
  width: max-content;
  overflow: hidden;
  max-width: 100%;
`;

const DisabledCheckboxOverlay = styled.span`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: 22px;
  z-index: 1;
  &:after {
    content: '';
    position: absolute;
    width: 10000%;
    top: 0;
    bottom: 0;
  }
`;

export function CheckboxGroup<TOptionValue>({
  disabled = false,
  size = 'm',
  options,
  value: values,
  onChange,
  minChecked = 0,
  minCheckedTooltip = '',
}: React.PropsWithChildren<Props<TOptionValue>>): JSX.Element {
  const handleChange =
    (value: TOptionValue) =>
    ({ target: { checked } }: React.ChangeEvent<HTMLInputElement>) => {
      const selected = options
        .filter((option) => {
          if (option.value === value) {
            return checked;
          }

          return values.includes(option.value);
        })
        .map((option) => option.value);

      onChange(selected);
    };
  const isDisabled = (forceChecked: boolean, isSelected: boolean) => {
    if (forceChecked) {
      return isSelected;
    }

    return disabled;
  };
  const isSelected = (value: TOptionValue) => values.findIndex(is(value)) !== -1;

  return (
    <List>
      {options.map(({ value, label }) => {
        const selected = isSelected(value);
        const forceChecked = values.length <= minChecked;
        const isOptionDisabled = isDisabled(forceChecked, selected);

        return (
          <ListItem key={String(value)}>
            {forceChecked && isOptionDisabled && (
              <Tooltip content={minCheckedTooltip} placement="top">
                <DisabledCheckboxOverlay />
              </Tooltip>
            )}
            <Checkbox
              size={size}
              disabled={isOptionDisabled}
              onChange={handleChange(value)}
              checked={selected}
              partial={false}
            >
              {label}
            </Checkbox>
          </ListItem>
        );
      })}
    </List>
  );
}
